import { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Divider,
    MenuItem,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
} from '@mui/material';
import { axiosInstance } from '../common/axiosInstans';
import { useGlobalStore } from '../application/store/store';
import packageJson from '../../package.json';

interface SupportInfo {
    streetAddress?: string;
    postalNumber?: string;
    city?: string;
    country?: string;
    openingHours?: string;
    phoneNumber?: string;
    email?: string;
}

export default function Support() {
    const [info, setInfo] = useState<SupportInfo>({});
    const [type, setType] = useState<
        'General Feedback' | 'Bug report' | 'Feature request'
    >('General Feedback');
    const [category, setCategory] = useState('User interface');
    const [priority, setPriority] = useState<
        'Low' | 'Medium' | 'High' | 'Critical'
    >('Medium');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [stepsToReproduce, setStepsToReproduce] = useState('');
    const [expectedBehavior, setExpectedBehavior] = useState('');
    const [actualBehavior, setActualBehavior] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const profile = useGlobalStore((state) => state.profile);
    const derivedUserName =
        `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim();
    const derivedUserEmail = profile?.email ?? '';
    const appVersion = packageJson.version;

    useEffect(() => {
        axiosInstance
            .get('/support')
            .then((res) =>
                setInfo({
                    streetAddress:
                        res.data.streetAddress ?? res.data.StreetAddress,
                    postalNumber:
                        res.data.postalNumber ?? res.data.PostalNumber,
                    city: res.data.city ?? res.data.City,
                    country: res.data.country ?? res.data.Country,
                    openingHours:
                        res.data.openingHours ?? res.data.OpeningHours,
                    phoneNumber: res.data.phoneNumber ?? res.data.PhoneNumber,
                    email: res.data.email ?? res.data.Email,
                })
            )
            .catch(() => setInfo({}));
    }, []);

    const handleSubmit = async () => {
        setIsSending(true);
        setSent(false);
        setError('');
        try {
            await axiosInstance.post('/feedback', {
                type,
                category,
                priority,
                title,
                description,
                steps_to_reproduce: stepsToReproduce,
                expected_behavior: expectedBehavior,
                actual_behavior: actualBehavior,
                user_name: derivedUserName,
                user_email: derivedUserEmail,
                timestamp: new Date().toISOString(),
                app_version: appVersion,
            });
            setSent(true);
            setTitle('');
            setDescription('');
            setStepsToReproduce('');
            setExpectedBehavior('');
            setActualBehavior('');
        } catch (e: any) {
            setError(
                e?.response?.data?.message ||
                    e?.response?.data ||
                    'Något gick fel vid skickande av feedback.'
            );
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, margin: '0 auto' }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                Support
            </Typography>
            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Typography variant="h6">Kontaktinformation</Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                            {info.streetAddress && (
                                <>
                                    {info.streetAddress}
                                    <br />
                                </>
                            )}
                            {info.postalNumber || info.city ? (
                                <>
                                    {info.postalNumber} {info.city}
                                    <br />
                                </>
                            ) : null}
                            {info.country}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 500 }}
                            >
                                E-post
                            </Typography>
                            <Typography variant="body2">
                                {info.email || '-'}
                            </Typography>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 500 }}
                            >
                                Telefon
                            </Typography>
                            <Typography variant="body2">
                                {info.phoneNumber || '-'}
                            </Typography>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 500 }}
                            >
                                Öppettider
                            </Typography>
                            <Typography variant="body2">
                                {info.openingHours || '-'}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <Typography variant="h6">Skicka feedback</Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormLabel>Typ</FormLabel>
                                    <RadioGroup
                                        row
                                        value={type}
                                        onChange={(e) =>
                                            setType(
                                                e.target.value as
                                                    | 'General Feedback'
                                                    | 'Bug report'
                                                    | 'Feature request'
                                            )
                                        }
                                    >
                                        <FormControlLabel
                                            value="General Feedback"
                                            control={<Radio />}
                                            label="Allmän feedback"
                                        />
                                        <FormControlLabel
                                            value="Bug report"
                                            control={<Radio />}
                                            label="Felrapport"
                                        />
                                        <FormControlLabel
                                            value="Feature request"
                                            control={<Radio />}
                                            label="Funktionsförslag"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            {(type === 'General Feedback' ||
                                type === 'Feature request') && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Kategori"
                                            value={category}
                                            onChange={(e) =>
                                                setCategory(e.target.value)
                                            }
                                        >
                                            <MenuItem value="General">
                                                Allmänt
                                            </MenuItem>
                                            <MenuItem value="Barcode scanning">
                                                Streckkodsskanning
                                            </MenuItem>
                                            <MenuItem value="Inventory management">
                                                Lagerhantering
                                            </MenuItem>
                                            <MenuItem value="Dose bag management">
                                                Dospåshantering
                                            </MenuItem>
                                            <MenuItem value="Authentication">
                                                Inloggning
                                            </MenuItem>
                                            <MenuItem value="User interface">
                                                Användargränssnitt
                                            </MenuItem>
                                            <MenuItem value="Performance">
                                                Prestanda
                                            </MenuItem>
                                            <MenuItem value="Other">
                                                Övrigt
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Prioritet"
                                            value={priority}
                                            onChange={(e) =>
                                                setPriority(
                                                    e.target.value as
                                                        | 'Low'
                                                        | 'Medium'
                                                        | 'High'
                                                        | 'Critical'
                                                )
                                            }
                                        >
                                            <MenuItem value="Low">Låg</MenuItem>
                                            <MenuItem value="Medium">
                                                Medium
                                            </MenuItem>
                                            <MenuItem value="High">
                                                Hög
                                            </MenuItem>
                                            <MenuItem value="Critical">
                                                Kritisk
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Titel"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Beskrivning"
                                    multiline
                                    minRows={3}
                                    value={description}
                                    onChange={(e) =>
                                        setDescription(e.target.value)
                                    }
                                />
                            </Grid>
                            {type === 'Bug report' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Steg för att återskapa"
                                            multiline
                                            minRows={3}
                                            value={stepsToReproduce}
                                            onChange={(e) =>
                                                setStepsToReproduce(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Förväntat beteende"
                                            multiline
                                            minRows={3}
                                            value={expectedBehavior}
                                            onChange={(e) =>
                                                setExpectedBehavior(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Faktiskt beteende"
                                            multiline
                                            minRows={3}
                                            value={actualBehavior}
                                            onChange={(e) =>
                                                setActualBehavior(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} sm={6} />
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        mt: 1,
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        disabled={
                                            isSending ||
                                            !title ||
                                            !description ||
                                            (type === 'Bug report' &&
                                                (!stepsToReproduce ||
                                                    !expectedBehavior ||
                                                    !actualBehavior))
                                        }
                                        onClick={handleSubmit}
                                    >
                                        {isSending
                                            ? 'Skickar...'
                                            : 'Skicka feedback'}
                                    </Button>
                                    {sent && (
                                        <Typography
                                            variant="body2"
                                            color="success.main"
                                        >
                                            Tack, din feedback har skickats.
                                        </Typography>
                                    )}
                                    {error && (
                                        <Typography
                                            variant="body2"
                                            color="error"
                                        >
                                            {error}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Din feedback hjälper oss att förbättra LogMed. All
                            information behandlas konfidentiellt.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
